import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ResponsiveContainer, ResponsiveHeader, ResponsiveContent, ResponsiveFooter } from '../ui/ResponsiveLayout';
import {
  ImportantDocumentsIcon,
  PhysicalAssetsIcon,
  CollectiblesAndValuablesIcon,
  ElectronicsIcon,
  NewDigitalAssetsIcon,
  CustomItemIcon,
  DocumentsIcon
} from '../ui/Icons';
import Modal from '../ui/Modal';
import { useSessionPersistence } from '../../hooks/useSessionPersistence';
import FileUpload from '../ui/FileUpload';
import { documentService } from '../../services/documentService';
import type { AppDocument } from '../../types/document';

  interface PersonalItem {
    id: string;
    category: 'documents' | 'physical_assets' | 'collectibles' | 'electronics' | 'digital_assets' | 'custom';
    name: string;
    description: string;
    estimatedValue?: number;
    accountNumber?: string;
    location: string;
    handlingInstructions: string;
    notes: string;
    createdAt: string;
    documents?: AppDocument[];
    assetType?: 'vehicle' | 'property' | 'title' | 'other';
    digitalType?: 'social_media' | 'email' | 'digital_media' | 'membership' | 'other';
    collectibleType?: 'art' | 'antique' | 'jewelry' | 'watch' | 'other';
  }

  const normalizeDocuments = (docs: unknown): AppDocument[] => {
    return documentService.normalizeMany(docs);
  };

  const Frame801: React.FC = () => {
    const navigate = useNavigate();
    const { session, updateTaskProgress, completeTask } = useSessionPersistence();

    const [items, setItems] = useState<PersonalItem[]>([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalTitle, setModalTitle] = useState('Add Personal Item');
    const [selectedCategory, setSelectedCategory] = useState<PersonalItem['category']>('custom');
    const [selectedSubType, setSelectedSubType] = useState<string>('');

    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [estimatedValue, setEstimatedValue] = useState('');
    const [accountNumber, setAccountNumber] = useState('');
    const [location, setLocation] = useState('');
    const [handlingInstructions, setHandlingInstructions] = useState('');
    const [notes, setNotes] = useState('');
    const [editingItemId, setEditingItemId] = useState<string | null>(null);

    // Store uploaded documents using the centralized document type
    const [documents, setDocuments] = useState<AppDocument[]>([]);
    const [isUploadingDocuments, setIsUploadingDocuments] = useState(false);

    const saveToLocalStorage = (itemsData: PersonalItem[]) => {
      try {
        localStorage.setItem('personal-items-backup', JSON.stringify(itemsData));
        localStorage.setItem(
          'personal-items-data-backup',
          JSON.stringify({
            personalItems: itemsData,
            lastUpdated: new Date().toISOString(),
            taskId: 'personal'
          })
        );
        console.log('[Frame801] ✅ IMMEDIATE SAVE - Personal items saved to localStorage backup');
      } catch (error) {
        console.error('[Frame801] âŒ IMMEDIATE SAVE FAILED:', error);
      }
    };

    const updatePersonalItems = (newItems: PersonalItem[]) => {
      console.log('[Frame801] Updating personal items with dual-save mechanism:', newItems);

      setItems(newItems);
      saveToLocalStorage(newItems);

      updateTaskProgress('personal', { personalItems: newItems })
        .then(() => {
          console.log('[Frame801] ✅ Session save successful');
        })
        .catch((error) => {
          console.warn('[Frame801] âš ï¸ Session save failed (non-critical):', error);
        });
    };

    useEffect(() => {
      if (session?.taskProgress?.personal?.answers) {
        const savedData = session.taskProgress.personal.answers.personalItems;
        if (savedData) {
          console.log('[Frame801] Loading from session:', savedData);
          setItems(
            savedData.map((item: PersonalItem) => ({
              ...item,
              documents: normalizeDocuments(item.documents || []),
            }))
          );
          return;
        }
      }

      try {
        const backupData = localStorage.getItem('personal-items-backup');
        if (backupData) {
          const data = JSON.parse(backupData);
          if (data) {
            console.log('[Frame801] Loading from localStorage backup:', data);
            setItems(
              data.map((item: PersonalItem) => ({
                ...item,
                documents: normalizeDocuments(item.documents || []),
              }))
            );
          }
        }
      } catch (error) {
        console.error('[Frame801] Failed to load from backup:', error);
      }
    }, [session?.taskProgress?.personal]);

    const openDocument = (doc: AppDocument) => {
      if (!doc.id) return;
      documentService.open(doc.id);
    };

    const downloadDocument = async (doc: AppDocument) => {
      if (!doc.id) return;

      try {
        await documentService.download(doc.id, doc.name);
      } catch (error) {
        console.error('[Frame801] Failed to download document:', error);
        alert('Failed to download document.');
      }
    };

    const formatCurrencyInput = (val: string) => {
      const digits = val.replace(/[^0-9]/g, '');
      if (!digits) return '';
      return parseInt(digits, 10).toLocaleString();
    };

    const resetModal = () => {
      setName('');
      setDescription('');
      setEstimatedValue('');
      setAccountNumber('');
      setLocation('');
      setHandlingInstructions('');
      setNotes('');
      setSelectedCategory('custom');
      setSelectedSubType('');
      setEditingItemId(null);
      setDocuments([]);
      setIsUploadingDocuments(false);
    };

    const openModal = (category: PersonalItem['category'], title: string) => {
      resetModal();
      setSelectedCategory(category);
      setModalTitle(title);
      setModalOpen(true);
    };

    const handleOk = async () => {
      if (!name.trim()) {
        alert('Item name is required');
        return;
      }

      const categoryConfig = categories.find(c => c.key === selectedCategory);
      if (categoryConfig?.subTypes && !selectedSubType) {
        alert(`Please select a ${categoryConfig.label.replace('\n', ' ')} type`);
        return;
      }

      const value = categoryConfig?.hideValue
        ? undefined
        : parseFloat(estimatedValue.replace(/,/g, '') || '0');

      const processedDocuments = normalizeDocuments(documents);

      if (editingItemId) {
        const updatedItems = items.map(item =>
          item.id === editingItemId
            ? {
                ...item,
                category: selectedCategory,
                name: name.trim(),
                description: description.trim(),
                ...(value !== undefined && { estimatedValue: value }),
                accountNumber: accountNumber.trim(),
                location: location.trim(),
                handlingInstructions: handlingInstructions.trim(),
                notes: notes.trim(),
                documents: processedDocuments,
                ...(selectedSubType && {
                  ...(selectedCategory === 'physical_assets' && { assetType: selectedSubType as unknown }),
                  ...(selectedCategory === 'digital_assets' && { digitalType: selectedSubType as unknown }),
                  ...(selectedCategory === 'collectibles' && { collectibleType: selectedSubType as unknown })
                })
              }
            : item
        );

        updatePersonalItems(updatedItems);
        console.log('[Frame801] Personal item updated with centralized docs:', editingItemId);
      } else {
        const newItem: PersonalItem = {
          id: Date.now().toString(),
          category: selectedCategory,
          name: name.trim(),
          description: description.trim(),
          ...(value !== undefined && { estimatedValue: value }),
          accountNumber: accountNumber.trim(),
          location: location.trim(),
          handlingInstructions: handlingInstructions.trim(),
          notes: notes.trim(),
          createdAt: new Date().toISOString(),
          documents: processedDocuments,
          ...(selectedSubType && {
            ...(selectedCategory === 'physical_assets' && { assetType: selectedSubType as unknown }),
            ...(selectedCategory === 'digital_assets' && { digitalType: selectedSubType as unknown }),
            ...(selectedCategory === 'collectibles' && { collectibleType: selectedSubType as unknown })
          })
        };

        const updatedItems = [...items, newItem];
        updatePersonalItems(updatedItems);
        console.log('[Frame801] Personal item added with centralized docs:', newItem);
      }

      setModalOpen(false);
    };

    const handleCancel = () => {
      setModalOpen(false);
    };

    const handleEditItem = (item: PersonalItem) => {
      setSelectedCategory(item.category);
      setName(item.name);
      setDescription(item.description);

      if (item.estimatedValue !== undefined) {
        setEstimatedValue(item.estimatedValue.toString());
      } else {
        setEstimatedValue('');
      }

      setAccountNumber(item.accountNumber || '');
      setLocation(item.location);
      setHandlingInstructions(item.handlingInstructions);
      setNotes(item.notes);
      setEditingItemId(item.id);
      setModalTitle(`Edit ${item.name}`);
      setDocuments(normalizeDocuments(item.documents || []));

      if (item.category === 'physical_assets' && item.assetType) {
        setSelectedSubType(item.assetType);
      } else if (item.category === 'digital_assets' && item.digitalType) {
        setSelectedSubType(item.digitalType);
      } else if (item.category === 'collectibles' && item.collectibleType) {
        setSelectedSubType(item.collectibleType);
      } else {
        setSelectedSubType('');
      }

      setModalOpen(true);
    };

    const handleDeleteItem = async (itemId: string) => {
      if (!confirm('Are you sure you want to delete this item?')) return;

      const itemToDelete = items.find(item => item.id === itemId);
      const attachedDocs = normalizeDocuments(itemToDelete?.documents || []);

      for (const doc of attachedDocs) {
        try {
          await documentService.delete(doc.id);
        } catch (error) {
          console.error('[Frame801] Failed to delete document while deleting item:', error);
        }
      }

      const updatedItems = items.filter(item => item.id !== itemId);
      updatePersonalItems(updatedItems);
    };

    const handleRemoveAttachedDocument = async (itemId: string, documentIndex: number) => {
      const item = items.find(entry => entry.id === itemId);
      const docToRemove = item?.documents?.[documentIndex];

      if (!docToRemove) return;

      try {
        await documentService.delete(docToRemove.id);
      } catch (error) {
        console.error('[Frame801] Failed to delete saved document:', error);
        alert('Failed to delete document.');
        return;
      }

      const updatedItems = items.map(entry => {
        if (entry.id !== itemId) return entry;
        return {
          ...entry,
          documents: normalizeDocuments(entry.documents || []).filter((_, index) => index !== documentIndex)
        };
      });

      updatePersonalItems(updatedItems);
    };

    const removeDocumentFromUploadModal = async (index: number) => {
      const docToRemove = documents[index];
      if (!docToRemove) return;

      try {
        await documentService.delete(docToRemove.id);
      } catch (error) {
        console.error('[Frame801] Failed to delete unsaved document:', error);
        alert('Failed to delete document.');
        return;
      }

      setDocuments(prev => prev.filter((_, i) => i !== index));
    };

    const handleNext = async () => {
      updateTaskProgress('personal', { personalItems: items });

      if (items.length > 0) {
        console.log('[Frame801] Marking personal belongings task as complete');
        await completeTask('personal');
        console.log('[Frame801] Personal belongings task marked as complete');
      }

      navigate('/frame-802');
    };

    const categories = [
      {
        key: 'documents' as const,
        label: 'Important\nDocuments',
        Icon: ImportantDocumentsIcon,
        description: 'Birth certificate, SS card, marriage certificate, divorce agreements, medical records, military records',
        hideValue: true
      },
      {
        key: 'physical_assets' as const,
        label: 'Physical\nAssets',
        Icon: PhysicalAssetsIcon,
        description: 'Vehicles, property, titles, and other physical assets',
        subTypes: [
          { value: 'vehicle', label: 'Vehicle' },
          { value: 'property', label: 'Property' },
          { value: 'title', label: 'Title' },
          { value: 'other', label: 'Other' }
        ]
      },
      {
        key: 'collectibles' as const,
        label: 'Collectibles &\nValuables',
        Icon: CollectiblesAndValuablesIcon,
        description: 'Art, antiques, jewelry, watches, and other collectibles',
        subTypes: [
          { value: 'art', label: 'Art' },
          { value: 'antique', label: 'Antique' },
          { value: 'jewelry', label: 'Jewelry' },
          { value: 'watch', label: 'Watch' },
          { value: 'other', label: 'Other' }
        ]
      },
      {
        key: 'electronics' as const,
        label: 'Electronics',
        Icon: ElectronicsIcon,
        description: 'Computers, phones, tablets, and other valuable electronics'
      },
      {
        key: 'digital_assets' as const,
        label: 'Digital\nAssets',
        Icon: NewDigitalAssetsIcon,
        description: 'Social media accounts, email, digital media services, memberships',
        subTypes: [
          { value: 'social_media', label: 'Social Media' },
          { value: 'email', label: 'Email' },
          { value: 'digital_media', label: 'Digital Media Services' },
          { value: 'membership', label: 'Membership' },
          { value: 'other', label: 'Other' }
        ]
      },
      {
        key: 'custom' as const,
        label: 'Add Custom\nItem',
        Icon: CustomItemIcon,
        description: 'Add any other personal belonging not covered by other categories'
      }
    ];

    const getIconForCategory = (category: PersonalItem['category']) => {
      const categoryConfig = categories.find(c => c.key === category);
      return categoryConfig ? categoryConfig.Icon : CustomItemIcon;
    };

    const categoryConfig = categories.find(c => c.key === selectedCategory);

    return (
      <ResponsiveContainer>
        <ResponsiveContent>
          <ResponsiveHeader
            title="Personal Belongings"
          />

          <div className="max-w-4xl">
            <div className="text-stone-800 text-lg font-medium font-sans mb-8">
              Let's start by documenting your personal belongings, accounts, and valuable items. Choose a category below to get started.
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
              {categories.map(({ key, label, Icon, description }) => (
                <button
                  key={key}
                  className="p-6 bg-white rounded-lg border border-stone-200 hover:border-cyan-500 transition-colors text-left"
                  onClick={() => {
                    const categoryConfig = categories.find(c => c.key === key);
                    openModal(key, categoryConfig?.label.replace('\n', ' ') || 'Add Item');
                  }}
                >
                  <div className="flex items-center gap-4">
                    <Icon className="w-8 h-8 text-cyan-600 flex-shrink-0" />
                    <div>
                      <h3 className="font-medium text-stone-800 whitespace-pre-line leading-tight">{label}</h3>
                      <p className="text-sm text-stone-600 mt-1">{description}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {items.length > 0 && (
              <div className="mt-8">
                <h2 className="text-lg font-medium text-stone-800 mb-4">Your Personal Items ({items.length})</h2>
                <div className={`space-y-4 ${items.length >= 3 ? 'max-h-56 min-h-0 overflow-y-auto' : ''}`}>
                  {items.map(item => {
                    const IconComponent = getIconForCategory(item.category);
                    const itemCategoryConfig = categories.find(c => c.key === item.category);
                    const metadataParts: string[] = [];

                    if (!itemCategoryConfig?.hideValue && item.estimatedValue !== undefined) {
                      metadataParts.push(`Value: $${item.estimatedValue.toLocaleString()}`);
                    }
                    if (item.location) {
                      metadataParts.push(`Location: ${item.location}`);
                    }

                    const subType = item.assetType || item.digitalType || item.collectibleType;
                    if (subType) {
                      metadataParts.push(`Type: ${subType.replace(/_/g, ' ')}`);
                    }

                    const attachedDocs = normalizeDocuments(item.documents || []);

                    return (
                      <div
                        key={item.id}
                        className="bg-white rounded-lg border border-neutral-200 p-4"
                      >
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">

                          <div className="flex flex-col gap-2 min-w-0 flex-1 sm:flex-row sm:items-start sm:gap-4">
                            <IconComponent className="w-6 h-6 text-cyan-700 flex-shrink-0" />

                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-stone-800 break-words">
                                {item.name}
                              </div>

                              {metadataParts.length > 0 && (
                                <div className="text-sm text-stone-500 mt-1 break-words">
                                  {metadataParts.join(' • ')}
                                </div>
                              )}

                              {item.description && (
                                <div className="text-sm text-stone-600 mt-2 break-words">
                                  {item.description}
                                </div>
                              )}

                              {item.notes && (
                                <div className="text-sm text-stone-600 mt-2 break-words">
                                  {item.notes}
                                </div>
                              )}

                              {item.handlingInstructions && (
                                <div className="text-sm text-stone-600 mt-2 break-words">
                                  {item.handlingInstructions}
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="flex flex-wrap gap-2 sm:flex-nowrap sm:shrink-0">
                            <button
                              onClick={() => handleEditItem(item)}
                              className="text-cyan-600 hover:text-cyan-700 px-3 py-1 rounded border border-cyan-200 hover:border-cyan-300"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteItem(item.id)}
                              className="text-red-600 hover:text-red-700 px-3 py-1 rounded border border-red-200 hover:border-red-300"
                            >
                              Delete
                            </button>
                          </div>

                        </div>

                        {attachedDocs.length > 0 && (
                          <div className="mt-4 space-y-2">
                            {attachedDocs.map((doc, index) => (
                              <div
                                key={`${item.id}-${doc.id}-${index}`}
                                className="flex flex-col gap-3 rounded-md border border-stone-200 bg-stone-50 px-3 py-3 sm:flex-row sm:items-center sm:justify-between"
                              >
                                <button
                                  type="button"
                                  onClick={() => openDocument(doc)}
                                  className="flex items-start gap-2 min-w-0 w-full text-left sm:flex-1"
                                >
                                  <DocumentsIcon className="w-4 h-4 text-cyan-700 flex-shrink-0 mt-0.5" />
                                  <span
                                    className="text-sm font-medium text-stone-800 truncate"
                                    title={doc.name}
                                  >
                                    {doc.name}
                                  </span>
                                </button>

                                <div className="flex flex-wrap items-center gap-x-3 gap-y-2 sm:shrink-0">
                                  <button
                                    type="button"
                                    onClick={() => downloadDocument(doc)}
                                    className="text-cyan-600 hover:text-cyan-800 text-xs font-medium"
                                  >
                                    Download
                                  </button>

                                  <button
                                    type="button"
                                    onClick={() => handleRemoveAttachedDocument(item.id, index)}
                                    className="text-red-600 hover:text-red-700 text-xs font-medium"
                                  >
                                    Remove
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </ResponsiveContent>
      
      <ResponsiveFooter
        title="Organize Personal Accounts and Belongings"
        subtitle="Add and organize your personal belongings"
        progressPercent={50}
        onBack={() => navigate('/frame-800')}
        onNext={handleNext}
        nextText="Next"
        backText="Back"
      />

        <Modal 
          open={modalOpen} 
          onClose={handleCancel}
          title={modalTitle}
          okText={editingItemId ? 'Update Item' : 'Add Item'}
          cancelText="Cancel"
          onOk={handleOk}
          onCancel={handleCancel}
        >
          <div className="space-y-4 pr-1">
            <div>
              <label className="block text-sm font-medium mb-1">Item Name *</label>
              <input
                value={name}
                onChange={e => setName(e.target.value)}
                className="w-full border border-neutral-300 rounded px-3 py-2"
                placeholder="e.g., Gold wedding ring, Checking account, Vintage guitar"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Description</label>
              <textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                rows={2}
                className="w-full border border-neutral-300 rounded px-3 py-2"
                placeholder="Brief description of the item"
              />
            </div>

            {categoryConfig?.subTypes && (
              <div>
                <label className="block text-sm font-medium mb-1">
                  {categoryConfig.label.replace('\n', ' ')} Type
                </label>
                <select
                  value={selectedSubType}
                  onChange={e => setSelectedSubType(e.target.value)}
                  className="w-full border border-neutral-300 rounded px-3 py-2"
                >
                  <option value="">Select type</option>
                  {categoryConfig.subTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {!categoryConfig?.hideValue && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Estimated Value ($)</label>
                  <input
                    value={estimatedValue}
                    onChange={e => setEstimatedValue(formatCurrencyInput(e.target.value))}
                    className="w-full border border-neutral-300 rounded px-3 py-2"
                    placeholder="0"
                  />
                </div>
                {selectedCategory === 'digital_assets' && (
                  <div>
                    <label className="block text-sm font-medium mb-1">Account Number/ID</label>
                    <input
                      value={accountNumber}
                      onChange={e => setAccountNumber(e.target.value)}
                      className="w-full border border-neutral-300 rounded px-3 py-2"
                      placeholder="Account identifier"
                    />
                  </div>
                )}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium mb-1">Location</label>
              <input
                value={location}
                onChange={e => setLocation(e.target.value)}
                className="w-full border border-neutral-300 rounded px-3 py-2"
                placeholder="e.g., Safe deposit box, Master bedroom dresser, Bank of America"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Handling Instructions</label>
              <textarea
                value={handlingInstructions}
                onChange={e => setHandlingInstructions(e.target.value)}
                rows={3}
                className="w-full border border-neutral-300 rounded px-3 py-2"
                placeholder="Special instructions for your loved ones about this item"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Additional Notes</label>
              <textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                rows={2}
                className="w-full border border-neutral-300 rounded px-3 py-2"
                placeholder="Any additional information"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Upload Documents</label>
              <FileUpload
                onUpload={async (files) => {
                  if (!files.length) return;

                  setIsUploadingDocuments(true);

                  try {
                    const uploadedDocs = await Promise.all(
                      files.map((file) => documentService.upload(file))
                    );
                    setDocuments(prev => [...prev, ...uploadedDocs]);
                  } catch (error) {
                    console.error('[Frame801] Failed to upload document(s):', error);
                    alert('Failed to upload one or more documents.');
                  } finally {
                    setIsUploadingDocuments(false);
                  }
                }}
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                multiple
              />

              {isUploadingDocuments && (
                <div className="mt-3 text-sm text-stone-500">
                  Uploading documents...
                </div>
              )}

              {documents.length > 0 && (
                <ul className="mt-4 space-y-1">
                  {documents.map((doc, index) => (
                    <li
                      key={`${doc.id}-${index}`}
                      className="flex flex-col sm:flex-row sm:items-center justify-between border border-neutral-200 rounded px-3 py-2 bg-white gap-3"
                    >
                      <button
                        type="button"
                        onClick={() => openDocument(doc)}
                        className="flex items-start gap-2 min-w-0 w-full sm:flex-1 text-left"
                      >
                        <DocumentsIcon className="w-4 h-4 text-cyan-700 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-stone-700 truncate" title={doc.name}>{doc.name}</span>
                      </button>

                      <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
                        <button
                          type="button"
                          onClick={() => downloadDocument(doc)}
                          className="text-cyan-600 hover:text-cyan-800 text-xs font-medium"
                        >
                          Download
                        </button>

                        <button
                          onClick={() => removeDocumentFromUploadModal(index)}
                          className="text-red-500 text-sm font-medium hover:text-red-700 ml-auto"
                        >
                          Remove
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </Modal>
      </ResponsiveContainer>
    );
  };

  export default Frame801;